import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators, UntypedFormGroup } from '@angular/forms';
import { BillableServiceValidators } from 'src/app/core/validators/billable-service.validators';
import { Observable } from 'rxjs';
import { BillableEdit } from '../model/billableEdit';
import { sortBy } from 'lodash';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Code } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { AccumulatorService } from 'src/app/core/service/accumulator.service';
import { ExtensionService } from 'src/app/core/service/extension.service';

@Component({
  standalone: false,
  selector: 'app-billable-edit',
  templateUrl: './billable-edit.component.html',
  styleUrls: ['./billable-edit.component.scss']
})
export class BillableEditComponent implements OnInit
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<BillableEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public dateRangeError: string;
  public datesOverlap: string;
  public feeSchedules: Code[] = [];
  public feeCategories: Code[] = [];
  public accumulators: any[] = [];
  public accumulatorsFiltered: any[] = [];
  public clientExtensions = [];
  public clientExtensionsFiltered: any[] = [];
  public virtualClientExtension: any = { itemHeight: 30 };

  public searchTerm: string;
  
  public maxRateDate: Date = new Date(2099, 11, 30, 23, 59, 59, 999);

  constructor(
    private fb: UntypedFormBuilder,
    private billableValidators: BillableServiceValidators,
    private translate: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private accumulatorService: AccumulatorService,
    private dictionaryService: DictionaryService,
    private extensionService: ExtensionService,) { }

  public get feeSchedule(): UntypedFormControl
  {
    return this.editForm.get('feeSchedule');
  }

  public get feeCategory(): UntypedFormControl
  {
    return this.editForm.get('feeCategory');
  }

  public get accumulator(): UntypedFormControl
  {
    return this.editForm.get('accumulator');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get enabled(): UntypedFormControl
  {
    return this.editForm.get('enabled');
  }

  public get manualAmount(): UntypedFormControl
  {
    return this.editForm.get('manualAmount');
  }

  public get informationalOnly(): UntypedFormControl
  {
    return this.editForm.get('informationalOnly');
  }

  @Input() public set model(model: BillableEdit)
  {
    this.initModel(model);
  }

  public ngOnInit(): void
  {
    this.dictionaryService.getCachedFeeScheduless().subscribe({
      next: (data) =>
      {
        this.feeSchedules = data;
      }
    });
    this.dictionaryService.getCachedFeeCategories().subscribe({
      next: (data) =>
      {
        this.feeCategories = data;
      }
    });
    this.accumulatorService.getAccumulatorList().subscribe({
      next: (data) =>
      {
        this.accumulators = data;
        this.filterAccumulators('');
      }
    });
    this.extensionService.getExtensionsByCategory('PATIENT', true, true).subscribe({
      next: (data) =>
      {
        this.clientExtensions = data;
        this.filterClientExtensions('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public filterClientExtensions(filter: string): void
  {
    this.searchTerm = filter;
    this.clientExtensionsFiltered = this.clientExtensions
      .filter(extension => extension.name.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Filter the accumulators based on user input.
   */
  public filterAccumulators(filterValue: string): void
  {
    this.accumulatorsFiltered = sortBy(this.accumulators
      .filter(accumulator => accumulator.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public accumulatorDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled;
  }

  public onSave(e: Event): void
  {
    if (this.informationalOnly.value)
    {
      this.accumulator.setValue(null);
    }
    else
    {
      // check End Date must be greater than Start Date
      for (let i = 0; i < this.editForm.controls.billableItems.controls.length; i++)
      {
        if (this.editForm.controls.billableItems.controls[i].controls.from.value !== null &&
          this.editForm.controls.billableItems.controls[i].controls.to.value !== null)
        {
          const d1 = moment(this.editForm.controls.billableItems.controls[i].controls.to.value);
          const d2 = moment(this.editForm.controls.billableItems.controls[i].controls.from.value);
          const diffMins = d1.diff(d2, 'minutes');
          if (diffMins < 0)
          {
            const rate = this.editForm.controls.billableItems.controls[i].controls.rate.value;
            this.messageHandlerService.displayDirectMessage('error', this.dateRangeError.replace('{{rate}}', rate));
            return;
          }
        }
      }

      // check date periods overlap
      for (let i = 0; i < this.editForm.controls.billableItems.controls.length; i++)
      {
        if (this.editForm.controls.billableItems.controls[i].controls.from.value !== null &&
          this.editForm.controls.billableItems.controls[i].controls.to.value !== null)
        {
          for (let j = i + 1; j < this.editForm.controls.billableItems.controls.length; j++)
          {
            if (this.editForm.controls.billableItems.controls[j].controls.from.value !== null &&
              this.editForm.controls.billableItems.controls[j].controls.to.value !== null)
            {
              const d1 = moment(this.editForm.controls.billableItems.controls[i].controls.to.value);
              const d2 = moment(this.editForm.controls.billableItems.controls[j].controls.from.value);
              if (d1 >= d2)
              {
                const rate1 = this.editForm.controls.billableItems.controls[i].controls.rate.value;
                const rate2 = this.editForm.controls.billableItems.controls[j].controls.rate.value;
                this.messageHandlerService.displayDirectMessage('error',
                  this.datesOverlap.replace('{{rate1}}', rate1).replace('{{rate2}}', rate2));
                return;
              }
            }
          }
        }
      }
    }

    e.preventDefault();
    const objectToPersist = this.editForm.value;

    // Clean up billable items
    this.setBillableItems(objectToPersist);

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public addBillableItem(id: number = null, code: string = null, rate: number = null, modifierCode: string = null, modifierCode2: string = null, modifierCode3: string = null,
    diagnosisCode: string = null, from: Date = null, to: Date = null, extensions: any = null, version = 0): void
  {
    this.editForm.controls.billableItems.push(this.initBillableItem(id, code, rate, modifierCode, modifierCode2, modifierCode3, diagnosisCode, from, to, extensions, version));
    this.editForm.markAsDirty();
  }

  public removeBillableItem(i: number): void
  {
    const control = this.editForm.controls.billableItems;
    control.removeAt(i);
    this.editForm.touched = true;
    this.editForm.markAsDirty();
  }

  private setBillableItems(objectToPersist: any): void
  {
    if (this.informationalOnly.value)
    {
      objectToPersist.billableItems = null;
      objectToPersist.manualAmount = false;
    }
    else
    {
      objectToPersist.billableItems.forEach((d: any) =>
      {
        if (this.manualAmount.value) { d.rate = null; } // Reset rate if manual amount
        delete d.rowId;
      });
    }
  }

  private initBillableItem(id = 0, code: string, rate: number, modifierCode: string, modifierCode2: string, modifierCode3: string,
    diagnosisCode: string, from: Date, to: Date, extensions: any, version: number): UntypedFormGroup
  {
    const rateValidator = [];
    if (!this.manualAmount.value)
    {
      rateValidator.push(Validators.required);
    }

    const maxDateValidator = (ctrl: UntypedFormControl) =>
      (!ctrl.value || new Date(ctrl.value) <= this.maxRateDate) ? null : { maxDate: true };

    return this.fb.group({
      rowId: null,
      id: [id],
      code: [code, Validators.required],
      rate: [rate, rateValidator],
      modifierCode: [modifierCode],
      modifierCode2: [modifierCode2],
      modifierCode3: [modifierCode3],
      diagnosisCode: [diagnosisCode],
      from: [from, [Validators.required, maxDateValidator]],
      to: [to, [Validators.required, maxDateValidator]],
      extensions: [extensions],
      version: [version]
    });
  }

  private validateRateDateControls(): void
  {
    if (!this.editForm?.controls?.billableItems?.controls) return;
    const items = this.editForm.controls.billableItems.controls;
    for (let i = 0; i < items.length; i++)
    {
      const group = items[i] as UntypedFormGroup;
      group.controls.from?.updateValueAndValidity();
      group.controls.to?.updateValueAndValidity();
    }
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(billableEdit: BillableEdit)
  {
    this.translate.get(['DATE-RANGE-ERROR-BILLABLE-ITEM', 'DATES-OVERLAP']).subscribe({
      next: (res) =>
      {
        this.dateRangeError = res['DATE-RANGE-ERROR-BILLABLE-ITEM'];
        this.datesOverlap = res['DATES-OVERLAP'];
      }
    });

    if (billableEdit === undefined)
    {
      return;
    }

    // Set the current billable used for name async validators
    const previousBillableServiceName = this.isNew ? '' : billableEdit.name;

    // Set form
    this.editForm = this.fb.group({
      id: [billableEdit.id],
      version: [billableEdit.version],
      feeSchedule: [{ value: billableEdit.feeSchedule, disabled: false }, Validators.required],
      feeCategory: [{ value: billableEdit.feeCategory, disabled: false }, Validators.required],
      accumulator: [{ value: billableEdit.accumulator, disabled: false }],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.billableValidators.validateBillableServiceNameUnique(this.editForm.get('name'), previousBillableServiceName)
        }
      ],
      description: { value: billableEdit.description, disabled: false },
      enabled: [billableEdit.enabled !== null ? billableEdit.enabled : true, Validators.required],
      manualAmount: [billableEdit.manualAmount !== null ? billableEdit.manualAmount : true, Validators.required],
      informationalOnly: [billableEdit.informationalOnly !== null ? billableEdit.informationalOnly : true, Validators.required],
      billableItems: this.fb.array([]),
      externalCode: [{ value: '', disabled: false }],
    });

    if (billableEdit && billableEdit.billableItems)
    {
      billableEdit.billableItems = sortBy(billableEdit.billableItems, ['from']);
      billableEdit.billableItems.forEach(data =>
      {
        data.from = data.from !== null ? new Date(`${data.from}T12:00:00.000`) : null;
        data.to = data.to !== null ? new Date(`${data.to}T12:00:00.000`) : null;
        this.addBillableItem(data.id, data.code, data.rate, data.modifierCode, data.modifierCode2, data.modifierCode3, data.diagnosisCode, data.from, data.to, data.extensions, data.version);
      });
      this.validateRateDateControls();
    }

    this.editForm.get('manualAmount').valueChanges.subscribe({ next: () => { this.setRateRequirementsAndControlsState(); } });
    this.editForm.get('informationalOnly').valueChanges.subscribe({ next: () => { this.setRateRequirementsAndControlsState(); } });
    this.setRateRequirementsAndControlsState();

    this.editForm.reset(billableEdit);
    this.editFormActive = billableEdit !== undefined;
  }

  private setRateRequirementsAndControlsState(): void 
  {
    for (let i = 0; i < this.editForm.controls.billableItems.controls.length; i++) 
    {
      if (this.manualAmount.value || this.informationalOnly.value)
      {
        this.editForm.controls.billableItems.controls[i].get('rate').setValidators([]);
      }
      else
      {
        this.editForm.controls.billableItems.controls[i].get('rate').setValidators([Validators.required]);
      }
      this.editForm.controls.billableItems.controls[i].get('rate').updateValueAndValidity();
    }
    this.setControlsState();
  }

  private setControlsState(): void
  {
    // Set controls state
    if (this.informationalOnly.value)
    {
      this.manualAmount.setValue(false, { emitEvent: false });
      this.manualAmount.disable({ emitEvent: false });
    }
    else
    {
      this.manualAmount.enable({ emitEvent: false });
    }

    if (this.manualAmount.value)
    {
      this.informationalOnly.setValue(false, { emitEvent: false });
      this.informationalOnly.disable({ emitEvent: false });
    }
    else
    {
      this.informationalOnly.enable({ emitEvent: false });
    }
  }
}
